import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React, { useMemo } from "react";
import {
    isStoryWordResolvedForPlay,
    resolveCanonicalWordKeyForStory,
    storyTokenMatchesHiddenMessagePhrase,
} from "../story/storyPlayUtils";

/** Compare story tokens to puzzle lexicon ignoring case and combining accents (Latin). */
function foldWordForMatch(value) {
    return String(value ?? "")
        .replace(/\ufeff|\u200b/gu, "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/\p{M}/gu, "");
}

function puzzleWordAliasSet(wordObjs) {
    const s = new Set();
    for (const w of wordObjs) {
        if (w.text != null && String(w.text).trim()) s.add(foldWordForMatch(w.text));
        if (w.displayText != null && String(w.displayText).trim()) {
            s.add(foldWordForMatch(w.displayText));
        }
    }
    return s;
}

/** How many gray tiles to render (graphemes when Segmenter exists, else code points). */
function visibleCharCount(word) {
    if (typeof Intl !== "undefined" && typeof Intl.Segmenter === "function") {
        const seg = new Intl.Segmenter(undefined, { granularity: "grapheme" });
        return [...seg.segment(word)].length;
    }
    return Array.from(word).length;
}

/**
 * Letters + apostrophes inside a token (French / Unicode). Keeps contractions like l'esprit as one chunk.
 * Build a fresh RegExp each pass so `.lastIndex` is never shared across cards / snippets.
 */
function createWordTokenRe() {
    return new RegExp("[\\p{L}']+", "gu");
}

/**
 * Split `text` into alternating gap strings vs word-token strings.
 */
function *iterStoryPieces(text) {
    const str = text == null ? "" : String(text);
    let last = 0;
    const WORD_TOKEN_RE = createWordTokenRe();
    let m = WORD_TOKEN_RE.exec(str);
    while (m !== null) {
        if (m.index > last) yield { kind: "gap", raw: str.slice(last, m.index) };
        yield { kind: "word", raw: m[0] };
        last = m.index + m[0].length;
        m = WORD_TOKEN_RE.exec(str);
    }
    if (last < str.length) yield { kind: "gap", raw: str.slice(last) };
}

function mergeFilledCoordinatesArrayIntoWords(arr, add) {
    if (!Array.isArray(arr)) return;
    for (const wordObj of arr) {
        if (wordObj?.text == null) continue;
        const t = String(wordObj.text).trim();
        if (!t) continue;
        const displayText = wordObj.reversed ? t.split("").reverse().join("") : t;
        add(t, displayText);
    }
}

function parseFilledCoordinatesAnything(fcRaw, add) {
    if (Array.isArray(fcRaw)) {
        mergeFilledCoordinatesArrayIntoWords(fcRaw, add);
        return;
    }
    if (typeof fcRaw !== "string") return;
    try {
        const arr = JSON.parse(fcRaw);
        mergeFilledCoordinatesArrayIntoWords(arr, add);
    } catch {
        /* ignore */
    }
}

/** Read wordsInUse from template row (camelCase or snake_case, string newline or string[]). */
function takeWordsInUseFromTpl(tpl) {
    if (!tpl || typeof tpl !== "object") return null;
    return tpl.wordsInUse ?? tpl.words_in_use ?? null;
}

function filledCoordsFromTpl(tpl) {
    if (!tpl || typeof tpl !== "object") return null;
    return tpl.filledCoordinates ?? tpl.filled_coordinates ?? null;
}

function appendLexiconBlob(raw, add) {
    if (raw == null) return;
    if (Array.isArray(raw)) {
        raw.forEach((line) => {
            const x = String(line ?? "").trim();
            if (x) add(x, x);
        });
        return;
    }
    if (typeof raw === "string") {
        raw.split(/\r?\n/).forEach((line) => {
            const x = line.trim().replace(/\ufeff|\u200b/g, "");
            if (x) add(x, x);
        });
    }
}

/** Topic blobs often mirror puzzle vocabulary when `/story` payloads omit nested `crossTpl`. */
function appendTopicLexicon(ro, add) {
    const topic = ro?.topic && typeof ro.topic === "object" ? ro.topic : null;
    if (!topic) return;
    appendLexiconBlob(topic.relatedWords ?? topic.related_words ?? null, add);
}

/**
 * Puzzle-only tokens (embedded template + coords). Omits topic.relatedWords so callers can decide
 * when to fetch `/home/puzzle/:id` for list rows stripped of tpl lexicon.
 */
export function collectTemplateWordObjsForStoryMask(ro) {
    return collectWordObjsInternal(ro, { topicRelatedWords: false, topRelatedWordsField: false });
}

/**
 * Full mask set for story UI: tpl + topic.relatedWords (+ optional top-level relatedWords field).
 */
export function collectWordObjsForStoryMask(ro) {
    return collectWordObjsInternal(ro, { topicRelatedWords: true, topRelatedWordsField: true });
}

function collectWordObjsInternal(ro, { topicRelatedWords, topRelatedWordsField }) {
    const out = [];
    const seen = new Set();
    const add = (text, displayText) => {
        const t = String(text ?? "").trim().replace(/\ufeff|\u200b/g, "");
        if (!t) return;
        const d = String(displayText ?? t).trim().replace(/\ufeff|\u200b/g, "");
        const key = `${foldWordForMatch(t)}\0${foldWordForMatch(d)}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({ text: t, displayText: d });
    };

    const tpls = [
        ro?.crossTpl,
        ro?.crossTemplate,
        ro?.cross_template,
        ro?.crossword?.crossTpl,
        ro?.crossword?.cross_tpl,
        ro?.gridTpl,
        ro?.gridTemplate,
        ro?.grid?.gridTpl,
        ro?.grid?.grid_tpl,
        ro?.hwTpl,
        ro?.hwGridTpl,
        ro?.hw_grid_tpl,
        ro?.template,
    ].filter((x) => x && typeof x === "object");

    for (const tpl of tpls) {
        appendLexiconBlob(takeWordsInUseFromTpl(tpl), add);
        parseFilledCoordinatesAnything(filledCoordsFromTpl(tpl), add);
    }

    appendLexiconBlob(ro?.wordsInUse ?? ro?.words_in_use ?? null, add);
    parseFilledCoordinatesAnything(ro?.filledCoordinates ?? ro?.filled_coordinates ?? null, add);

    if (topicRelatedWords) appendTopicLexicon(ro, add);
    if (topRelatedWordsField) appendLexiconBlob(ro?.relatedWords ?? null, add);

    return out;
}

function grayBoxesForWord(rawWord, keySuffix) {
    const n = Math.max(0, visibleCharCount(rawWord));
    return Array.from({ length: n }, (_, i) => (
        <Box
            component="span"
            key={`${keySuffix}-${i}`}
            sx={{
                width: "0.55em",
                height: "1em",
                minWidth: "6px",
                bgcolor: "grey.500",
                borderRadius: "2px",
                display: "inline-block",
                flexShrink: 0,
                boxSizing: "border-box",
                verticalAlign: "text-bottom",
            }}
        />
    ));
}

/** Shared green underline for puzzle words in story text (storybook reader, hints). */
export const PUZZLE_WORD_UNDERLINE_SX = {
    textDecoration: "underline",
    textDecorationColor: "#3BA666",
    textDecorationThickness: "2px",
    textUnderlineOffset: "0.18em",
    fontWeight: 600,
};

/** Bold + underline for puzzle words (e.g. word-of-day story accordion). */
export const PUZZLE_WORD_BOLD_UNDERLINE_SX = {
    ...PUZZLE_WORD_UNDERLINE_SX,
    fontWeight: 700,
};

/** Underline + fill when a crossword/grid word is solved during play. */
export const puzzleWordResolvedHighlightSx = (theme, variant = "green") => ({
    ...PUZZLE_WORD_UNDERLINE_SX,
    ...(variant === "blue" && {
        textDecorationColor: "#1565c0",
    }),
    bgcolor:
        variant === "blue"
            ? theme.palette.mode === "dark"
                ? "rgba(33, 150, 243, 0.35)"
                : "#BBDEFB"
            : theme.palette.mode === "dark"
              ? "rgba(76, 175, 80, 0.38)"
              : "#C4FFE0",
    borderRadius: 1,
    px: 0.25,
    boxDecorationBreak: "clone",
    WebkitBoxDecorationBreak: "clone",
});

/**
 * Story text with puzzle vocabulary highlighted.
 * @param {"mask"|"underline"|"boldUnderline"} [highlightMode="mask"] — gray tiles, underline, or bold+underline.
 * @param {Array} [filledCoordinates] — with `resolvedWordsFound`, maps story tokens to grid words for solve state.
 * @param {string[]|null} [resolvedWordsFound] — underline mode: solved words also get a green highlight.
 * @param {{ revealed: boolean, phrase: string }|null} [hiddenMessageStoryGate] — hide the secret phrase in prose until the puzzle is complete.
 * @param {"green"|"blue"} [resolvedHighlightVariant="green"] — highlight fill for solved words (hidden message uses blue).
 * @param {string} [underlineColor] — override underline color (e.g. blue for hidden message).
 */
export function MaskedStorySnippet({
    text,
    wordObjs,
    emptyLabel,
    highlightMode = "mask",
    filledCoordinates = null,
    resolvedWordsFound = null,
    hiddenMessageStoryGate = null,
    resolvedHighlightVariant = "green",
    underlineColor,
    ...typographyProps
}) {
    const content = useMemo(() => {
        const trimmed = String(text ?? "").trim();
        if (!trimmed) return emptyLabel ?? null;
        if (!wordObjs?.length) return trimmed;
        const keys = puzzleWordAliasSet(wordObjs);
        const pieces = [];
        const useUnderline = highlightMode === "underline" || highlightMode === "boldUnderline";
        const underlineSx = {
            ...(highlightMode === "boldUnderline" ? PUZZLE_WORD_BOLD_UNDERLINE_SX : PUZZLE_WORD_UNDERLINE_SX),
            ...(underlineColor ? { textDecorationColor: underlineColor } : {}),
        };
        const trackResolved =
            useUnderline &&
            Array.isArray(resolvedWordsFound) &&
            Array.isArray(filledCoordinates) &&
            filledCoordinates.length > 0;

        let pieceIdx = 0;
        for (const chunk of iterStoryPieces(trimmed)) {
            if (chunk.kind === "gap") {
                if (chunk.raw) pieces.push(chunk.raw);
                continue;
            }

            const w = chunk.raw;
            const folded = foldWordForMatch(w);
            const match = folded && keys.has(folded);

            if (match) {
                const suffix = `${pieceIdx++}:${folded}:${w}`;
                const hmGate = hiddenMessageStoryGate;
                const tokenIsHmPhrase =
                    hmGate &&
                    typeof hmGate.phrase === "string" &&
                    String(hmGate.phrase).trim() !== "" &&
                    storyTokenMatchesHiddenMessagePhrase(w, hmGate.phrase);

                if (useUnderline) {
                    if (tokenIsHmPhrase && !hmGate.revealed) {
                        pieces.push(
                            <Box
                                key={`hm-${suffix}`}
                                component="span"
                                sx={{
                                    display: "inline-flex",
                                    gap: "2px",
                                    alignItems: "center",
                                    verticalAlign: "text-bottom",
                                    mx: "1px",
                                }}
                            >
                                {grayBoxesForWord(w, suffix)}
                            </Box>
                        );
                        continue;
                    }

                    let resolved = false;
                    if (tokenIsHmPhrase && hmGate.revealed) {
                        resolved = true;
                    } else if (trackResolved) {
                        const canonical = resolveCanonicalWordKeyForStory(w, filledCoordinates);
                        if (canonical) {
                            resolved = isStoryWordResolvedForPlay(canonical, resolvedWordsFound);
                        }
                    }
                    pieces.push(
                        <Box
                            key={`u-${suffix}`}
                            component="span"
                            sx={
                                resolved
                                    ? (theme) =>
                                          puzzleWordResolvedHighlightSx(theme, resolvedHighlightVariant)
                                    : underlineSx
                            }
                        >
                            {w}
                        </Box>
                    );
                } else {
                    pieces.push(
                        <Box
                            key={`h-${suffix}`}
                            component="span"
                            sx={{
                                display: "inline-flex",
                                gap: "2px",
                                alignItems: "center",
                                verticalAlign: "text-bottom",
                                mx: "1px",
                            }}
                        >
                            {grayBoxesForWord(w, suffix)}
                        </Box>
                    );
                }
            } else {
                pieces.push(w);
            }
        }

        return pieces;
    }, [
        text,
        wordObjs,
        emptyLabel,
        highlightMode,
        filledCoordinates,
        resolvedWordsFound,
        hiddenMessageStoryGate,
        resolvedHighlightVariant,
        underlineColor,
    ]);

    return <Typography {...typographyProps}>{content}</Typography>;
}
